// UsernameField.tsx
import { useEffect, useMemo, useState } from 'react';
import debounce from 'lodash.debounce';
import { Icon, TextInput } from '../common';
import { checkUsername } from '../../api/auth';
import { useTranslation } from '../../hooks/translations/TranslationProvider';

type UsernameFieldProps = {
    value: string;
    onValueChange: (value: string) => void;
    formError: string | null;
    onValidationChange?: (hasError: boolean) => void; // <- add
    delay?: number;
};

const USERNAME_REGEX = /^[a-zA-Z0-9._-]+$/;

const UsernameField = ({
    value,
    onValueChange,
    onValidationChange,
    formError,
    delay = 400,
}: UsernameFieldProps) => {
    const [localValue, setLocalValue] = useState(value);
    const [error, setError] = useState<string | null>(null);
    const [isValidUsername, setIsValidUsername] = useState(false);
    const { t } = useTranslation();

    useEffect(() => setLocalValue(value), [value]);

    const onUsernameChange = async (username: string) => {
        if (!username.trim()) {
            setError(null);
            setIsValidUsername(false);
            return;
        }
        if (username.length < 3) {
            setError(t('errors.shortUsername'));
            setIsValidUsername(false);
            onValidationChange?.(true);
            return;
        }
        if (!USERNAME_REGEX.test(username)) {
            setError(t('errors.invalidUsername'));
            setIsValidUsername(false);
            onValidationChange?.(true);
            return;
        }

        try {
            await checkUsername(username);

            setIsValidUsername(true);
            onValidationChange?.(false);
        } catch (e: any) {
            if (e.response.status === 400) {
                setError(t('errors.invalidUsernameFull'));
                onValidationChange?.(true);
            }

            if (e.response.status === 409) {
                setError(t('errors.takenUsername'));
                onValidationChange?.(true);
            }
        }
    };

    const debouncedUsernameChange = useMemo(
        () => debounce((v: string) => onUsernameChange(v), delay),
        [delay]
    );

    useEffect(
        () => () => debouncedUsernameChange.cancel(),
        [debouncedUsernameChange]
    );

    const handleChange = (_: string, next: string) => {
        setLocalValue(next);
        onValueChange(next); // parent form state
        setError(null);
        setIsValidUsername(false);
        debouncedUsernameChange(next); // component owns validation logic
    };

    const shownError = error || (formError && !localValue ? formError : null);

    return (
        <div className="register-username-input">
            <TextInput
                name="username"
                label="Username"
                value={localValue}
                onChange={handleChange}
                className="full-width"
                autoComplete="off"
                error={shownError}
                placeholder="Username"
            />

            {error && <Icon name="bx-error-circle" color="red" size="18px" />}

            {!error && isValidUsername && (
                <Icon name="bx-check-circle" color="green" size="18px" />
            )}
        </div>
    );
};

export default UsernameField;
